/**
 * NETOBS_COLORS — single color-token export for the netcode-observability
 * arc. Authority dots (NO-1), packet sparkline (NO-3), tick badge (NO-2),
 * cast strip (NO-4), heatmap (NO-7), scene overlay (NO-8) all read from
 * here.
 *
 * Naming + rationale: `docs/arcs/netcode-observability/03-NAMING-CONVENTIONS.md`
 *
 * 4 distinct hues for the 4 authority types (color-blindness friendly:
 * no red-green pair conveys authority alone). Health uses the standard
 * traffic-light + black for catastrophic. Direction uses blue/yellow so a
 * direction column doesn't get confused with an authority column.
 */

export const NETOBS_COLORS = {
  // ── Authority (entity ownership) ─────────────────────────────────
  AUTH_LOCAL:   '#42d77d',  // green — you own input
  AUTH_REMOTE:  '#4ea3ff',  // blue — other human, server-driven
  AUTH_BOT:     '#a960ff',  // purple — server AI, sid prefix bot_
  AUTH_NPC:     '#ff9f3f',  // orange — server state-machine

  // ── Health (tick / packet thresholds) ────────────────────────────
  HEALTH_OK:    '#42d77d',  // green — under budget
  HEALTH_WARN:  '#ffd23f',  // yellow — 50-90% budget
  HEALTH_BAD:   '#ff5f5f',  // red — over budget
  HEALTH_DEAD:  '#1a1a1a',  // black — multiple buckets over budget

  // ── Cast pipeline strip (NO-4) ──────────────────────────────────
  CAST_INTENT:    '#94a3b8',  // slate
  CAST_GATE:      '#ffd23f',  // yellow
  CAST_EXECUTE:   '#42d77d',  // green
  CAST_WIRE:      '#4ea3ff',  // blue
  CAST_CONFIRMED: '#a960ff',  // purple
  CAST_REJECTED:  '#ff5f5f',  // red (overrides whichever stage rejected)

  // ── Direction (packet log) ──────────────────────────────────────
  DIR_IN:  '#4ea3ff',  // blue (server → me)
  DIR_OUT: '#ffd23f',  // yellow (me → server)
} as const;

export type NetobsColorKey = keyof typeof NETOBS_COLORS;

/**
 * Pick a health color for a value vs. budget. Buckets:
 *  - <50% budget          → OK (green)
 *  - 50-90% budget        → WARN (yellow)
 *  - 90-200% budget       → BAD (red)
 *  - >200% budget         → DEAD (black)
 */
export function netobsHealthColor(value: number, budget: number): string {
  if (budget <= 0) return NETOBS_COLORS.HEALTH_OK;
  const ratio = value / budget;
  if (ratio < 0.5) return NETOBS_COLORS.HEALTH_OK;
  if (ratio < 0.9) return NETOBS_COLORS.HEALTH_WARN;
  if (ratio < 2.0) return NETOBS_COLORS.HEALTH_BAD;
  return NETOBS_COLORS.HEALTH_DEAD;
}
